// src/components/how-it-works/HowItWorksPage.tsx
// Public page explaining ShiYou's two core flows (no auth required)

import React, { useState, useEffect } from 'react';
import BackgroundShapes from '../common/BackgroundShapes';
import CondoCard from '../condos/CondoCard';
import DiscoverUserCard from '../discover/DiscoverUserCard';
import { condoService } from '../../services/condoService';
import { discoverAPI } from '../../services/discoverService';
import { Condo } from '../../types/condo';
import { DiscoverUser } from '../../types/discover';

// ── Feature highlight data ────────────────────────────────────────────────────

interface FeatureCard {
  emoji: string;
  title: string;
  description: string;
}

const FEATURE_CARDS: FeatureCard[] = [
  {
    emoji: '🏢',
    title: 'Interest Groups',
    description:
      'Post the group link anywhere — FB, RedNote, Threads. Everyone lands in one place.',
  },
  {
    emoji: '🔍',
    title: 'Filters That Find Your Fit',
    description:
      'Set location, budget, lifestyle, move-in date. The wrong people disappear.',
  },
  {
    emoji: '🤖',
    title: 'AI Compatibility Score',
    description:
      'Profiles ranked by how well they match your lifestyle. No guessing.',
  },
];

// ── Step strip data ───────────────────────────────────────────────────────────

const CONDO_STEPS = [
  'Find a listing',
  'Join the group',
  'Share the link',
  'Watch your group fill up',
];

const ROOMMATE_STEPS = [
  'Set your filters',
  'Browse ranked profiles',
  'Send an invite',
];

// ── Sub-components ────────────────────────────────────────────────────────────

const StepStrip: React.FC<{ steps: string[] }> = ({ steps }) => (
  <div className="flex flex-col sm:flex-row items-stretch gap-3 mb-8">
    {steps.map((label, i) => (
      <React.Fragment key={label}>
        {/* Step card — flex-1 + items-stretch ensures uniform height across all cards */}
        <div className="flex-1 bg-cream border-2 border-coffee rounded-xl p-5 flex flex-col items-center text-center shadow-[2px_2px_0px_#594A42]">
          <div className="w-9 h-9 rounded-full bg-terracotta text-white border-2 border-coffee flex items-center justify-center font-bold text-sm shadow-[2px_2px_0px_#594A42] mb-3 flex-shrink-0">
            {i + 1}
          </div>
          <span className="text-coffee text-sm font-medium leading-snug">{label}</span>
        </div>
        {/* Arrow between steps — ↓ on mobile, → on desktop */}
        {i < steps.length - 1 && (
          <div className="flex items-center justify-center text-coffee/40 font-bold text-xl flex-shrink-0 self-center">
            <span className="sm:hidden">↓</span>
            <span className="hidden sm:block">→</span>
          </div>
        )}
      </React.Fragment>
    ))}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const structuredData = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to find a roommate with ShiYou",
  "description": "Browse NYC and Jersey City listings, join interest groups, and connect with AI-matched roommates.",
  "step": [
    { "@type": "HowToStep", "name": "Pick or create a listing", "position": 1 },
    { "@type": "HowToStep", "name": "Join the interest group", "position": 2 },
    { "@type": "HowToStep", "name": "Share the invitation URL", "position": 3 },
    { "@type": "HowToStep", "name": "Coordinate in ShiYou", "position": 4 },
  ],
};

const HowItWorksPage: React.FC = () => {
  const [condos, setCondos] = useState<Condo[]>([]);
  const [condosLoading, setCondosLoading] = useState(true);
  const [condosError, setCondosError] = useState<string | null>(null);

  const [users, setUsers] = useState<DiscoverUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch condos (independent of users fetch)
    condoService
      .listCondos(1, 3)
      .then((res) => {
        if (res && res.data && res.data.condos) {
          setCondos(res.data.condos);
        } else {
          setCondosError('No listings available right now.');
        }
      })
      .catch(() => {
        setCondosError('Could not load listings. Please try again later.');
      })
      .finally(() => setCondosLoading(false));

    // Fetch public users (independent of condos fetch)
    discoverAPI
      .browsePublicUsers({}, 1, 3)
      .then((res) => {
        if (res.success && res.data && res.data.length > 0) {
          setUsers(res.data);
        } else if (res.success) {
          setUsersError('No profiles available right now.');
        } else {
          setUsersError('Could not load profiles. Please try again later.');
        }
      })
      .catch(() => {
        setUsersError('Could not load profiles. Please try again later.');
      })
      .finally(() => setUsersLoading(false));
  }, []);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className="max-w-6xl mx-auto">

          {/* ── Hero ──────────────────────────────────────────────────────── */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-coffee mb-4">How ShiYou Works</h1>
            <p className="text-2xl text-coffee">Find people you'll actually want to live with</p>
          </div>

          {/* ── Section B: Feature highlights ─────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {FEATURE_CARDS.map((card) => (
              <div
                key={card.title}
                className="bg-cream border-2 border-coffee rounded-xl p-6 shadow-[4px_4px_0px_#594A42]"
              >
                <div className="text-3xl mb-3">{card.emoji}</div>
                <h3 className="text-lg font-bold text-coffee mb-2">{card.title}</h3>
                <p className="text-coffee/70 text-sm leading-relaxed">{card.description}</p>
              </div>
            ))}
          </div>

          <hr className="border-coffee/20 mb-14" />

          {/* ── Section: Find a Home ──────────────────────────────────────── */}
          <section className="mb-14">
            <h2 className="text-3xl font-bold text-coffee mb-6">🏢 One Listing, One Group</h2>

            <StepStrip steps={CONDO_STEPS} />

            {/* Live condo cards */}
            {condosLoading && (
              <div className="text-coffee/50 text-sm py-8 text-center">Loading...</div>
            )}
            {!condosLoading && condosError && (
              <div className="text-coffee/60 text-sm py-8 text-center">{condosError}</div>
            )}
            {!condosLoading && !condosError && condos.length === 0 && (
              <p className="text-coffee/50 text-sm text-center py-8">
                No listings available right now.
              </p>
            )}
            {!condosLoading && !condosError && condos.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {condos.map((condo) => (
                  <CondoCard
                    key={condo.id ?? condo._id}
                    condo={condo}
                    isAuthenticated={false}
                    onCheckWaitlist={() => {}}
                  />
                ))}
              </div>
            )}
          </section>

          <hr className="border-coffee/20 mb-14" />

          {/* ── Section: Find Roommates ───────────────────────────────────── */}
          <section className="mb-14">
            <h2 className="text-3xl font-bold text-coffee mb-6">👥 Find Your Best Match</h2>

            <StepStrip steps={ROOMMATE_STEPS} />

            {/* Live user cards */}
            {usersLoading && (
              <div className="text-coffee/50 text-sm py-8 text-center">Loading...</div>
            )}
            {!usersLoading && usersError && (
              <div className="text-coffee/60 text-sm py-8 text-center">{usersError}</div>
            )}
            {!usersLoading && !usersError && users.length === 0 && (
              <p className="text-coffee/50 text-sm text-center py-8">
                No profiles available right now.
              </p>
            )}
            {!usersLoading && !usersError && users.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {users.map((user) => (
                  <DiscoverUserCard
                    key={user.id}
                    user={user}
                    isAuthenticated={false}
                    onNavigate={() => {}}
                  />
                ))}
              </div>
            )}
          </section>

          <hr className="border-coffee/20 mb-14" />

          {/* ── CTA ───────────────────────────────────────────────────────── */}
          <div className="text-center py-6">
            <p className="text-coffee text-2xl font-bold mb-6">Ready to find your people?</p>
            <a
              href="/signup"
              className="inline-block bg-terracotta text-white px-10 py-4 rounded-xl font-bold text-xl border-2 border-coffee shadow-[4px_4px_0px_#594A42] hover:bg-[#d89050] transition-colors"
            >
              → Join ShiYou
            </a>
          </div>

        </div>
      </div>
    </div>
    </>
  );
};

export default HowItWorksPage;
